<template>
  <div>
    <!-- Note row -->
    <div
      class="cursor-pointer flex flex-row items-center gap-1 !justify-between dropdown_item note_item_row"
      :class="{ selected: isSelected || isDropdownOpen }"
      :style="{ paddingLeft: `${16 + depth * 14}px` }"
      @click="$emit('click', note)"
      @mouseenter="handleMouseEnter"
      @mouseleave="isHovered = false"
    >
      <!-- Expand/collapse chevron -->
      <div class="flex flex-row items-center gap-2 truncate flex-1 min-w-0">
        <div
          v-if="hasChildren"
          class="flex-shrink-0 size-4 flex items-center justify-center cursor-pointer"
          @click.stop="expanded = !expanded"
        >
          <svg
            width="10" height="10" viewBox="0 0 10 10" fill="currentColor"
            :style="{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', opacity: 0.5 }"
          >
            <path d="M3 2l4 3-4 3V2z" />
          </svg>
        </div>
        <div v-else class="flex-shrink-0 size-4" />

        <ThemeImage class="size-4 flex-shrink-0" :icon="imageNames.PANEL_NOTES" alt="note_icon" />

        <a-input
          v-if="isRenaming"
          ref="renameInputRef"
          class="edit_inputs text-sm"
          :value="renameValue"
          @change="(e) => { renameValue = e.target.value }"
          @blur="handleRenameSave"
          @keyup.enter="handleRenameSave"
          @keyup.escape="isRenaming = false"
          @click.stop
        />
        <a-tooltip v-else :title="isTruncated ? (note.name || 'Untitled Note') : ''" placement="top" color="var(--tooltip-bg)">
          <span ref="textRef" class="text-sm color_666 font-normal truncate">
            {{ note.name || "Untitled Note" }}
          </span>
        </a-tooltip>
      </div>

      <!-- Actions (hover) -->
      <div
        class="flex flex-row items-center gap-1 note_item_actions"
        :class="{ show: isDropdownOpen || isHovered }"
      >
        <a-tooltip placement="top" color="var(--tooltip-bg)">
          <template #title>
            <span class="text-[13px] font-normal primary_color">Add note inside</span>
          </template>
          <div class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper" @click.stop="handleAddSubNote">
            <ThemeImage class="size-4" :icon="imageNames.PLUS_GREY_THIN" alt="add_sub_note" />
          </div>
        </a-tooltip>

        <a-dropdown
          trigger="click"
          placement="bottomLeft"
          :get-popup-container="(trigger) => trigger.parentElement"
          @visibleChange="isDropdownOpen = $event"
        >
          <a-tooltip placement="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">More options</span>
            </template>
            <div class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper" :class="{ selected: isDropdownOpen }">
              <ThemeImage class="size-4" :icon="imageNames.MORE_ICON_GREY" alt="more_icon" />
            </div>
          </a-tooltip>

          <template #overlay>
            <a-menu class="dropdown_menu">
              <a-menu-item @click="startRename">
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage class="size-4" :icon="imageNames.EDIT" alt="rename_icon" />
                  <span class="text-sm primary_color">Rename</span>
                </div>
              </a-menu-item>
              <a-menu-item @click="showDeleteModal = true">
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage class="size-4" :icon="imageNames.DELETE_GREY" alt="delete_icon" />
                  <span class="text-sm primary_color">Delete</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </div>

    <!-- Children (recursive) -->
    <template v-if="hasChildren && expanded">
      <NoteTreeItem
        v-for="child in note.children"
        :key="child.id || child.key"
        :note="child"
        :depth="depth + 1"
        :selected-id="selectedId"
        @click="$emit('click', $event)"
      />
    </template>

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Note"
      :message="`Are you sure you want to delete ${note?.name || 'this note'}?`"
      :loading="isDeleting"
      type="delete"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
      confirm-text="Delete"
      loading-text="Deleting..."
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { noteEvents, renameNote } from "@/src/constants/noteConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import ProjectService from "@/src/api/ProjectService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";

const props = defineProps({
  note: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  selectedId: { type: String, default: null },
});

const emit = defineEmits(["click"]);

const isSelected = computed(() => props.selectedId === props.note?.id);
const hasChildren = computed(() => props.note?.children?.length > 0);

const expanded = ref(false);
const isHovered = ref(false);
const textRef = ref(null);
const isTruncated = ref(false);
const isDropdownOpen = ref(false);
const showDeleteModal = ref(false);
const isDeleting = ref(false);
const isRenaming = ref(false);
const renameValue = ref("");
const renameInputRef = ref(null);

const handleMouseEnter = () => {
  isHovered.value = true;
  if (textRef.value) {
    isTruncated.value = textRef.value.scrollWidth > textRef.value.clientWidth;
  }
};

const handleAddSubNote = () => {
  isDropdownOpen.value = false;
  eventBus.emit("note-add-sub-note-clicked", props.note);
};

const startRename = () => {
  isDropdownOpen.value = false;
  renameValue.value = props.note?.name || "";
  isRenaming.value = true;
  setTimeout(() => renameInputRef.value?.focus());
};

const handleRenameSave = async () => {
  const newName = renameValue.value?.trim();
  if (!newName || newName === props.note?.name) {
    isRenaming.value = false;
    return;
  }
  try {
    await renameNote(props.note?.id, newName, props.note?.entity_id);
  } catch (error) {
    console.error("Error renaming note:", error);
    message.error("An error occurred while renaming note!");
  } finally {
    isRenaming.value = false;
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;
    const response = await ProjectService.notes.deleteNote(props.note?.id);
    if (response?.success) {
      message.success("Note deleted successfully!");
      showDeleteModal.value = false;
      eventBus.emit("note-manipulated", { event: noteEvents.DELETED, spaceId: props.note?.entity_id });
    }
  } catch (error) {
    console.error("An error occurred while deleting a note:", error);
    message.error("An error occurred while deleting note!");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.note_item_actions {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}
.note_item_actions.show {
  opacity: 1;
  visibility: visible;
}
</style>
